import type { PageResult } from "@pdfking/pdfium"
import type { PDFConvertFunction, PDFSplitResult } from "@pdfking/renderer-api"
import type { MediaPixel } from "../types/MediaPixel"
import { BlobArray, BlobToUint8, ImportImage } from "@pdfking/compatibility"
import { ProvisionWasm } from "@pdfking/pdfium"
import { IterableToArray } from "@pdfking/tfunc"
import { AutoThrow } from "../utils/AutoThrow"

const ToSplitResult = (result: PageResult): PDFSplitResult => ({
  blob: BlobArray(result.bytes, "application/pdf"),
  ...(result.media as MediaPixel),
})

const rgbaToBgra = (data: Uint8Array): Uint8Array => {
  const out = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i += 4) {
    out[i] = data[i + 2]!
    out[i + 1] = data[i + 1]!
    out[i + 2] = data[i]!
    out[i + 3] = data[i + 3]!
  }
  return out
}

export const PDFConvert: PDFConvertFunction = async (input, resolution) => {
  for await (const result of ProvisionWasm(async function* (api) {
    const bytes = await IterableToArray(input.stream)
      .then(BlobArray)
      .then(BlobToUint8)

    // JPEG: embed directly
    if (bytes[0] === 0xff && bytes[1] === 0xd8) {
      yield ToSplitResult(AutoThrow(await api.convert_jpeg(bytes, resolution)))
      return
    }

    // Other formats: decode in browser → BGRA bitmap
    const { data, width, height } = await ImportImage(
      BlobArray(bytes, input.type)
    )
    const bmpResult = await api.convert_bitmap(
      rgbaToBgra(data),
      { width, height },
      resolution
    )
    yield ToSplitResult(AutoThrow(bmpResult))
  })) {
    return result
  }
  throw new Error("No result from ProvisionWasm")
}
